import React, { useState } from 'react';

// Markup shared by both History skins. Everything here is theme-neutral: the
// classes are defined once in HistoryTab.css and re-skinned by the
// .hst-root.classic / .hst-root.zone tokens around them.

/** Sticker icons live in skinsData (assets/all.json) keyed by `name`, since
 *  most sticker entries there have a null market_hash_name. An entry that
 *  already carries its own image URL wins — the extension can supply one
 *  straight from the marketplace and skip the lookup. */
// Imported for the Sticker strip below, then re-exported for the History pages
// that have always pulled it from this module.
import { findStickerImage } from './skinIndex';
export { findStickerImage } from './skinIndex';

/** 'Sticker | Titan (Holo) | Katowice 2014' → 'Titan (Holo)' + 'Katowice 2014'.
 *  The row is tight, so the team reads big and the event reads small. */
export function splitStickerName(name) {
  const parts = String(name || '').split('|').map(p => p.trim());
  if (parts[0]?.toLowerCase() === 'sticker') parts.shift();
  return { title: parts[0] || name || 'Unknown sticker', event: parts.slice(1).join(' · ') || null };
}

/** Scrape state. CSFloat reports `wear` as the fraction scraped away, with
 *  null meaning the sticker has never been scraped. */
export function stickerWearLabel(wear) {
  if (wear == null) return 'Pristine';
  return `${Math.round(wear * 100)}% scraped`;
}

const money = n => Math.abs(Number(n) || 0).toFixed(2);

function Sticker({ sticker, image }) {
  const [broken, setBroken] = useState(false);
  const { title, event } = splitStickerName(sticker.name);
  const scraped = sticker.wear != null;

  return (
    <div className="hst-sticker" title={sticker.name}>
      <span className="hst-slot" aria-label={`Position ${sticker.slot + 1}`}>{sticker.slot + 1}</span>

      {image && !broken
        ? <img src={image} alt="" loading="lazy" onError={() => setBroken(true)} />
        : <span className="hst-sticker-blank" aria-hidden="true" />}

      <span className="hst-sticker-txt">
        <span className="n">{title}</span>
        {event && <span className="e">{event}</span>}
      </span>

      <span className="hst-sticker-vals">
        <span className="p">{sticker.price != null ? `$${money(sticker.price)}` : 'n/a'}</span>
        <span className={`w${scraped ? ' scraped' : ''}`}>{stickerWearLabel(sticker.wear)}</span>
      </span>
    </div>
  );
}

/** The applied-sticker strip under a ledger row, in slot order. */
export function StickerStrip({ stickers = [], skinsData = [] }) {
  if (!stickers.length) return null;
  const ordered = [...stickers].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const total = ordered.reduce((s, x) => s + (x.price || 0), 0);

  return (
    <div className="hst-stickers">
      <span className="hst-stickers-label">
        Stickers
        {total > 0 && <b>${money(total)}</b>}
      </span>
      {ordered.map((s, i) => (
        <Sticker key={`${s.name}-${s.slot ?? i}`} sticker={s} image={findStickerImage(skinsData, s)} />
      ))}
    </div>
  );
}
